import { PlusIcon, Trash2Icon } from 'lucide-react';
import { useFieldArray, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import CardBase from '@/components/organisms/Integrations/Components/CardBase';
import { Button } from '@/components/ui/button';
import { URLInput } from '@/components/ui/custom/URLInput/URLInput';
import {
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { WebVoiceIntegration } from '@/shared/types/integration';

const Connection = ({ integration }: { integration: WebVoiceIntegration }) => {
  const { t } = useTranslation();
  const { control } = useFormContext<WebVoiceIntegration>();
  const { fields, append, remove } = useFieldArray<WebVoiceIntegration>({
    control,
    name: 'config.allowed_origins' as never,
  });

  return (
    <CardBase title={t('common.connection')} status={integration.status}>
      <FormItem>
        <FormLabel>{t('integrations.web_voice_connect.allowed_origins.title')}</FormLabel>
        <FormDescription>
          {t('integrations.web_voice_connect.allowed_origins.description')}
        </FormDescription>
        <div className="space-y-2">
          {fields.map((field, index) => (
            <FormField
              key={field.id}
              control={control}
              name={`config.allowed_origins.${index}` as const}
              render={({ field: itemField }) => (
                <FormItem>
                  <div className="flex items-center gap-2">
                    <FormControl>
                      <URLInput
                        {...itemField}
                        className="flex-1"
                        placeholder={t(
                          'integrations.web_voice_connect.allowed_origins.placeholder'
                        )}
                      />
                    </FormControl>
                    <Button
                      className="hover:text-destructive hover:[&_svg]:text-destructive"
                      type="button"
                      variant="ghost-neutral"
                      size="icon-sm"
                      onClick={() => remove(index)}
                      aria-label={t('common.remove')}
                    >
                      <Trash2Icon size={16} />
                    </Button>
                  </div>
                  <FormMessage />
                </FormItem>
              )}
            />
          ))}
          <Button
            type="button"
            variant="ghost-neutral"
            size="sm"
            onClick={() => append('' as never)}
          >
            <PlusIcon size={16} />
            {t('integrations.web_voice_connect.allowed_origins.add')}
          </Button>
        </div>
      </FormItem>

      <FormField
        control={control}
        name="config.ice_transport_policy"
        render={({ field }) => (
          <FormItem>
            <FormLabel>{t('integrations.web_voice_connect.ice_transport_policy.title')}</FormLabel>
            <FormControl>
              <RadioGroup value={field.value} onValueChange={field.onChange} className="mt-2">
                <RadioGroupItem
                  value="all"
                  id="ice_transport_policy_all"
                  description={t(
                    'integrations.web_voice_connect.ice_transport_policy.all.description'
                  )}
                >
                  {t('integrations.web_voice_connect.ice_transport_policy.all.label')}
                </RadioGroupItem>
                <RadioGroupItem
                  value="relay"
                  id="ice_transport_policy_relay"
                  description={t(
                    'integrations.web_voice_connect.ice_transport_policy.relay.description'
                  )}
                >
                  {t('integrations.web_voice_connect.ice_transport_policy.relay.label')}
                </RadioGroupItem>
              </RadioGroup>
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />
    </CardBase>
  );
};

export default Connection;
